// RD-ROLE-005b — field-role dollar-free reads (client wiring).
//
// Base `jobs` / `job_line_items` / `customers` SELECT now returns 0 rows to a
// blind field role (worker/supervisor with canViewPrices() === false). Those
// roles read the dollar-free `field_jobs` / `field_job_line_items` /
// `field_customers` views instead — mirroring the RD-ROLE-005 catalog
// pattern (`src/utils/catalog/catalogReadTables.js`) and the RD-AUD-013
// operations pattern (`src/utils/job/reshapeOperationsJobRow.js`), but
// toggleable per tenant rather than hardcoded to one role.
//
// Operations deliberately returns the BASE table names here — ops sites
// already hand-route to `operations_*` views (RD-AUD-013) and are untouched
// by this router; operations is money-blind on a DIFFERENT axis (no
// `total`/`unit_price` at all, ever) than the field-role price-visibility
// toggle this router serves.
//
// Prefer the hook path (`usePriceVisibility()` → `fieldReadTablesForRole`)
// inside React so a resolving CompanySettingsContext re-renders correctly.
// Non-hook (plain module) callers use `getFieldReadTables()`, which is
// store-backed (`priceVisibilityStore` + `currentTenantRoleStore`) so they
// don't need a hand-threaded `role`/`canSeePrices` param at every call site.

import { getCurrentTenantRole } from '../auth/currentTenantRoleStore';
import { getPriceVisibleFieldRoles } from '../auth/priceVisibilityStore';
import { canViewPrices } from './jobRecordAccess';

const BASE_TABLES = Object.freeze({ jobs: 'jobs', jobLineItems: 'job_line_items', customers: 'customers' });
const FIELD_TABLES = Object.freeze({ jobs: 'field_jobs', jobLineItems: 'field_job_line_items', customers: 'field_customers' });

/**
 * @param {string|null|undefined} role
 * @param {boolean} [canSeePrices=true]
 * @returns {{ jobs: string, jobLineItems: string, customers: string }}
 */
export function fieldReadTablesForRole(role, canSeePrices = true) {
  if ((role === 'worker' || role === 'supervisor') && !canSeePrices) {
    return FIELD_TABLES;
  }
  return BASE_TABLES;
}

/**
 * RD-LUI-004 — a stable string for a table set (sign-in prefetch key variant):
 * a prefetch made for one price-visibility fork never satisfies the other.
 * @param {{ jobs: string, jobLineItems: string, customers: string }} t
 */
export function fieldTablesKey(t) {
  return `${t?.jobs}|${t?.jobLineItems}|${t?.customers}`;
}

/**
 * Store-backed convenience for non-hook (plain module) callers — resolves
 * the current session's role + price-visibility setting from the mirrored
 * stores rather than requiring a hand-threaded param.
 * @returns {{ jobs: string, jobLineItems: string, customers: string }}
 */
export function getFieldReadTables() {
  const role = getCurrentTenantRole();
  const canSeePrices = canViewPrices(role, getPriceVisibleFieldRoles());
  return fieldReadTablesForRole(role, canSeePrices);
}

/**
 * Reshapes one `field_jobs` row back into the shape a base `jobs` query with
 * `customers (full_name, service_address)` / `profiles (full_name)` embeds
 * returns — PostgREST embeds don't resolve against a DEFINER view, so
 * `field_jobs` denormalizes the display columns instead. `total` and
 * `unpriced_line_count` simply stay absent (mirrors
 * `reshapeOperationsJobRow`). Null-safe: an unset denormalized column becomes
 * `null` on the nested object rather than throwing.
 *
 * @param {object} row a raw field_jobs row
 * @returns {object} the row reshaped to the base-table + embeds shape
 */
export function reshapeFieldJobRow(row) {
  if (!row) return row;
  const {
    customer_full_name,
    customer_service_address,
    customer_pending_follow_up,
    worker_full_name,
    ...rest
  } = row;
  return {
    ...rest,
    customers: {
      full_name: customer_full_name ?? null,
      service_address: customer_service_address ?? null,
      pending_follow_up: customer_pending_follow_up ?? null,
    },
    profiles: { full_name: worker_full_name ?? null },
  };
}

/** Array form — maps `reshapeFieldJobRow` over a jobs result set. */
export function reshapeFieldJobRows(rows) {
  return Array.isArray(rows) ? rows.map(reshapeFieldJobRow) : rows;
}
